import React, { useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import FormTextField from 'components/IAP/formFields/FormTextField';
import FormSelectField from 'components/IAP/formFields/FormSelectField';
import FormDateTimeField from 'components/IAP/formFields/FormDateTimeField';
import ResourceSelectFields from 'components/IAP/ResourceSelectFields';
import { Row } from 'react-bootstrap';
import { v4 as uuidv4 } from 'uuid';
import { ResourceRequestItem } from 'components/IAP/hooks/incidentResourceRequestHooks';
import { StylishNewButton } from 'components/DesignSystems/New/StylishNewButton';

type AddRequestedResourceProps = {
  setCustomResources: React.Dispatch<
    React.SetStateAction<ResourceRequestItem[]>
  >;
  setLogisticsItems?: React.Dispatch<
    React.SetStateAction<ResourceRequestItem[][]>
  >;
  workAssignment?: string | null;
  setShowAdd: React.Dispatch<React.SetStateAction<boolean>>;
  showOwningOrg?: boolean;
  resourceToEdit?: ResourceRequestItem;
  editIndex?: number | null;
  onClose?: () => void;
};

export const AddRequestedResource = ({
  setCustomResources,
  setLogisticsItems,
  workAssignment,
  setShowAdd,
  showOwningOrg = false,
  resourceToEdit,
  editIndex,
  onClose,
}: AddRequestedResourceProps) => {
  const initialValues = {
    ...resourceToEdit,
    name: resourceToEdit?.name ?? workAssignment ?? '',
  };

  const form = useForm<ResourceRequestItem>({
    mode: 'onChange',
    defaultValues: initialValues,
  });

  useEffect(() => {
    if (resourceToEdit) return;
    form.setValue('name', workAssignment ?? '');
  }, [workAssignment, resourceToEdit, form]);

  const closeForm = () => {
    if (onClose) {
      onClose();
      return;
    }

    setShowAdd(false);
  };

  const onSubmit = async (formData: ResourceRequestItem) => {
    if (editIndex !== null && editIndex !== undefined) {
      setCustomResources((prev) =>
        prev.map((resource, index) =>
          index === editIndex ? { ...formData, id: resource.id } : resource
        )
      );
      form.reset({ name: '' });
      closeForm();
      return;
    }

    setCustomResources((prev) => [...prev, { ...formData, id: uuidv4() }]);
    if (setLogisticsItems) {
      setLogisticsItems((prev) => [...prev, []]);
    }
    form.reset({ name: workAssignment ?? '' });
    closeForm();
  };

  const priorityOptions = [
    { value: 'low', label: 'Low' },
    { value: 'medium', label: 'Medium' },
    { value: 'high', label: 'High' },
    { value: 'critical', label: 'Critical' },
  ];
  return (
    <div className="border p-2 rounded mb-2">
      <FormProvider {...form}>
        <Row>
          <div className="col-1">
            <FormTextField
              title="Quantity"
              fieldName="quantity"
              rules={{
                required: { value: true, message: 'This field is required' },
                pattern: {
                  value: /^\d+$/,
                  message: 'This field must be a positive integer',
                },
              }}
            />
          </div>
          <div className="col-4">
            {/* TODO: Disabling Personnel for now until positions as resources is complete */}
            <ResourceSelectFields labels disableKind={'Personnel'} />
          </div>
          <div className="col-1">
            <FormSelectField
              title="Priority"
              fieldName="priority"
              options={priorityOptions}
              isClearable={true}
            />
          </div>
          <div className="col-6">
            <FormTextField
              title="Description*"
              fieldName="name"
              rules={{ required: 'Description is required' }}
            />
          </div>
        </Row>
        <Row>
          <div className="col-2">
            <FormTextField
              title="Requested Reporting Location"
              fieldName="requested_reporting_location"
            />
          </div>
          <div className="col-5">
            <FormDateTimeField
              title="Requested Report Date"
              fieldName="requested_reporting_datetime"
            />
          </div>
          <div className="col-5">
            <FormDateTimeField
              title="Requested Demob Date"
              fieldName="requested_demob_datetime"
            />
          </div>
          {showOwningOrg && (
            <div className="col-2">
              <FormTextField
                title="Owning Organization*"
                fieldName="owning_organization"
                rules={{ required: 'Owning Organization is required' }}
              />
            </div>
          )}
          <div>
            <StylishNewButton onClick={form.handleSubmit(onSubmit)}>
              {editIndex !== null && editIndex !== undefined
                ? 'Save Resource'
                : 'Add Resource'}
            </StylishNewButton>
            <StylishNewButton
              onClick={closeForm}
              className="ms-2 button--secondary"
            >
              Cancel
            </StylishNewButton>
          </div>
        </Row>
      </FormProvider>
    </div>
  );
};
